import { ReplaySubject, merge, race, timer, Subject } from 'rxjs';
import { takeUntil, startWith, switchMap, mapTo, shareReplay, filter, tap } from 'rxjs/operators';

import {
  Directive,
  ContentChildren,
  QueryList,
  HostListener,
  AfterContentInit,
  ElementRef,
  inject,
} from '@angular/core';

import { LifecycleHooks } from '@shared/services/lifecycle-hooks.service';

@Directive({
  selector: '[focusRetain]',
  standalone: false,
})
export class FocusRetain {
  readonly el = inject<ElementRef<HTMLInputElement>>(ElementRef);

  readonly blur$ = new Subject<UIEvent>();

  readonly focus$ = new Subject<UIEvent>();

  @HostListener('blur', ['$event'])
  onBlur(event: UIEvent) {
    this.blur$.next(event);
  }

  @HostListener('focus', ['$event'])
  onFocus(event: UIEvent) {
    this.focus$.next(event);
  }
}

@Directive({
  selector: '[focusControl]',
  providers: [LifecycleHooks],
  standalone: false,
})
export class FocusControl implements AfterContentInit {
  private lh = inject(LifecycleHooks);

  @ContentChildren(FocusRetain, { descendants: true })
  retainers: QueryList<FocusRetain>;

  private retainers$ = new ReplaySubject<FocusRetain[]>(1);

  private activeRetainer$ = this.retainers$.pipe(
    switchMap((retainers) =>
      merge(
        ...retainers.map((retainer) =>
          retainer.focus$.pipe(
            mapTo(retainer),
            startWith(retainer.el.nativeElement === document.activeElement ? retainer : null),
            filter((item) => !!item),
          ),
        ),
      ),
    ),
    shareReplay(1),
  );

  ngAfterContentInit(): void {
    this.retainers.changes
      .pipe(startWith(this.retainers.toArray()), takeUntil(this.lh.destroy))
      .subscribe((retainers: FocusRetain[]) => {
        this.retainers$.next(retainers);
      });

    this.activeRetainer$
      .pipe(
        switchMap((active) =>
          race(
            active.blur$.pipe(
              switchMap(() =>
                race(
                  this.activeRetainer$.pipe(filter((retainer) => retainer !== active)),
                  timer(10).pipe(tap(() => active.el.nativeElement.focus())),
                ),
              ),
            ),
            timer(500),
          ),
        ),
        takeUntil(this.lh.destroy),
      )
      .subscribe();
  }
}
